import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import { prepareEventImageForUpload, DEFAULT_PREPARE_IMAGE_OPTIONS } from '../Utils/prepareEventImageForUpload'
import { measureImageDimensions, measureImageUrl } from '../Utils/measureImageDimensions'
import { applyImagePositionToElement, initialCropArea, serializeImagePosition as serializeSharedImagePosition } from '../Utils/imagePosition'
import './AnearFramedImage'
import {
  assembleMedia,
  legacyFieldNamesFromName,
  normalizeMedia,
  serializeMedia
} from '../Utils/mediaValue'
import { contentColumnOverlaySx } from '../UI/contentColumn'

const DEFAULT_IMAGE_POSITION = { x: 50, y: 18, zoom: 1 }

function parseFloatAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseFloat(val)
  return Number.isFinite(n) ? n : fallback
}

function parseIntAttr(val, fallback) {
  if (val == null || val === '') return fallback
  const n = parseInt(val, 10)
  return Number.isFinite(n) ? n : fallback
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n))
}

function normalizeImagePosition(raw) {
  if (!raw) return { ...DEFAULT_IMAGE_POSITION }

  let parsed = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return { ...DEFAULT_IMAGE_POSITION }
    }
  }

  const x = Number(parsed?.x)
  const y = Number(parsed?.y)
  const zoom = Number(parsed?.zoom)
  return {
    x: Number.isFinite(x) ? clamp(x, 0, 100) : DEFAULT_IMAGE_POSITION.x,
    y: Number.isFinite(y) ? clamp(y, 0, 100) : DEFAULT_IMAGE_POSITION.y,
    zoom: Number.isFinite(zoom) ? clamp(zoom, 1, 3) : DEFAULT_IMAGE_POSITION.zoom
  }
}

function serializeImagePosition(position) {
  const normalized = normalizeImagePosition(position)
  return JSON.stringify({
    x: Math.round(normalized.x * 10) / 10,
    y: Math.round(normalized.y * 10) / 10,
    zoom: Math.round(normalized.zoom * 100) / 100
  })
}

function positionFromCroppedArea(croppedArea, zoom) {
  return {
    x: clamp(croppedArea.x + croppedArea.width / 2, 0, 100),
    y: clamp(croppedArea.y + croppedArea.height / 2, 0, 100),
    zoom
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not load image for cropping.'))
    img.src = url
  })
}

function canvasToBlob(canvas, mimeType, quality = 0.92) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Could not crop image.'))
      }
    }, mimeType, quality)
  })
}

async function cropImageFileForUpload(file, croppedAreaPixels) {
  if (!file || !croppedAreaPixels) return file

  const sourceUrl = URL.createObjectURL(file)
  try {
    const img = await loadImage(sourceUrl)
    const canvas = document.createElement('canvas')
    const width = Math.max(1, Math.round(croppedAreaPixels.width))
    const height = Math.max(1, Math.round(croppedAreaPixels.height))
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not crop image.')
    ctx.drawImage(
      img,
      Math.max(0, Math.round(croppedAreaPixels.x)),
      Math.max(0, Math.round(croppedAreaPixels.y)),
      width,
      height,
      0,
      0,
      width,
      height
    )

    const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
    const blob = await canvasToBlob(canvas, mimeType)
    return new File([blob], file.name, { type: mimeType })
  } finally {
    URL.revokeObjectURL(sourceUrl)
  }
}

function ImageCropModal({ imageUrl, initialPosition, aspect, title, onApply, onCancel }) {
  const normalizedInitial = useMemo(() => normalizeImagePosition(initialPosition), [initialPosition])
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(normalizedInitial.zoom)
  const [imageSize, setImageSize] = useState(undefined)
  const userAdjustedRef = useRef(false)
  const initialCroppedAreaPercentages = useMemo(() => {
    if (imageSize === undefined) return null
    return initialCropArea(normalizedInitial, normalizedInitial.zoom, imageSize ? {
      imageWidth: imageSize.width,
      imageHeight: imageSize.height,
      aspect
    } : {})
  }, [normalizedInitial, imageSize, aspect])
  const croppedAreaRef = useRef(null)
  const croppedAreaPixelsRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    measureImageUrl(imageUrl).then((size) => {
      if (!cancelled) setImageSize(size)
    })
    return () => { cancelled = true }
  }, [imageUrl])

  useEffect(() => {
    croppedAreaRef.current = initialCroppedAreaPercentages
  }, [initialCroppedAreaPercentages])

  const onCropAreaChange = useCallback((croppedArea, nextCroppedAreaPixels) => {
    croppedAreaRef.current = croppedArea
    croppedAreaPixelsRef.current = nextCroppedAreaPixels
  }, [])

  const markAdjusted = () => {
    userAdjustedRef.current = true
  }

  const handleApply = () => {
    const croppedArea = croppedAreaRef.current
    if (!croppedArea) return
    const next = positionFromCroppedArea(croppedArea, zoom)
    const unchanged = !userAdjustedRef.current
      || serializeSharedImagePosition(next) === serializeSharedImagePosition(normalizedInitial)
    onApply({
      position: unchanged ? normalizedInitial : next,
      croppedAreaPixels: croppedAreaPixelsRef.current
    })
  }

  return (
    <div style={styles.backdrop} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{title}</h2>
            <p style={styles.helpText}>Drag the photo to position it. Use zoom when the subject needs a tighter crop.</p>
          </div>
          <button type="button" style={styles.closeButton} onClick={onCancel} aria-label="Close photo editor">x</button>
        </div>
        <div
          style={styles.cropArea}
          onPointerDown={markAdjusted}
          onWheel={markAdjusted}
        >
          {initialCroppedAreaPercentages ? (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={3}
              aspect={aspect}
              restrictPosition
              initialCroppedAreaPercentages={initialCroppedAreaPercentages}
              onCropChange={setCrop}
              onCropAreaChange={onCropAreaChange}
              onZoomChange={setZoom}
            />
          ) : null}
        </div>
        <label style={styles.zoomLabel}>
          <span>Zoom</span>
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            onChange={(event) => {
              markAdjusted()
              setZoom(Number(event.target.value))
            }}
            style={styles.zoomInput}
          />
        </label>
        <div style={styles.actions}>
          <button type="button" style={styles.secondaryButton} onClick={onCancel}>Cancel</button>
          <button type="button" style={styles.primaryButton} onClick={handleApply} disabled={!initialCroppedAreaPercentages}>Apply Photo</button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    background: 'rgba(2, 6, 23, 0.72)',
    ...contentColumnOverlaySx
  },
  dialog: {
    width: 'min(34rem, 100%)',
    padding: '1rem',
    borderRadius: '1rem',
    background: '#ffffff',
    boxShadow: '0 1.5rem 4rem rgba(15, 23, 42, 0.28)',
    color: '#111827'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '0.75rem'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    lineHeight: 1.2
  },
  helpText: {
    margin: '0.25rem 0 0',
    color: '#5c6370',
    fontSize: '0.9rem',
    lineHeight: 1.35
  },
  closeButton: {
    alignSelf: 'flex-start',
    border: 0,
    background: 'transparent',
    fontSize: '1.6rem',
    lineHeight: 1,
    cursor: 'pointer'
  },
  cropArea: {
    position: 'relative',
    width: '100%',
    height: 'min(52vh, 24rem)',
    minHeight: '16rem',
    overflow: 'hidden',
    borderRadius: '0.75rem',
    background: '#0f172a'
  },
  zoomLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '0.9rem',
    color: '#374151',
    fontSize: '0.92rem'
  },
  zoomInput: {
    flex: 1
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.6rem',
    marginTop: '1rem'
  },
  primaryButton: {
    border: 0,
    borderRadius: '999px',
    padding: '0.65rem 1rem',
    background: '#2563eb',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer'
  },
  secondaryButton: {
    border: '1px solid #d1d5db',
    borderRadius: '999px',
    padding: '0.65rem 1rem',
    background: '#ffffff',
    color: '#374151',
    cursor: 'pointer'
  }
}

class AnearImageUpload extends HTMLElement {
  constructor() {
    super()
    this._onUploadRequest = null
    this._eventId = null
    this._fileInput = null
    this._hiddenInput = null
    this._filenameHiddenInput = null
    this._positionHiddenInput = null
    this._widthInput = null
    this._heightInput = null
    this._statusEl = null
    this._triggerBtn = null
    this._adjustBtn = null
    this._uploading = false
    this._previewObjectUrl = null
    this._editorObjectUrl = null
    this._modalContainer = null
    this._modalRoot = null
    this._pendingFile = null
    this._mediaState = null
  }

  connectedCallback() {
    this.render()
    this.syncInitialPreview()
  }

  initialize(onUploadRequest, eventId) {
    this._onUploadRequest = onUploadRequest
    this._eventId = eventId
  }

  disconnectedCallback() {
    this.closeEditor()
    this.releasePreviewObjectUrl()
    this.releaseEditorObjectUrl()
  }

  get fieldName() {
    return this.getAttribute('name') || 'uploadedImageUrl'
  }

  get filenameFieldName() {
    return this.getAttribute('filename-field') || ''
  }

  get widthFieldName() {
    return this.getAttribute('width-field') || ''
  }

  get heightFieldName() {
    return this.getAttribute('height-field') || ''
  }

  /**
   * Measuring costs an image decode, so only apps that asked for the numbers
   * pay for it. An app that sets neither attribute behaves exactly as before.
   */
  get dimensionsRequested() {
    return Boolean(this.widthFieldName || this.heightFieldName)
  }

  /**
   * Opaque media is preferred when crop-mode=position and AppM does not
   * declare position-field. Legacy split fields remain when position-field is set.
   */
  get opaqueMediaEnabled() {
    return this.cropPositionEnabled && !this.hasAttribute('position-field')
  }

  get positionFieldName() {
    // Explicit position-field keeps the legacy split-field wire format.
    // Otherwise crop-mode=position uses opaque media under `name`.
    if (!this.hasAttribute('position-field')) return ''
    return this.getAttribute('position-field') || legacyFieldNamesFromName(this.fieldName).position
  }

  resolveInitialMedia() {
    return assembleMedia({
      mediaValue: this.getAttribute('media-value'),
      url: this.getAttribute('value'),
      filename: this.getAttribute('filename-value'),
      position: this.getAttribute('position-value')
    })
  }

  syncOpaqueHiddenInput() {
    if (!this.opaqueMediaEnabled || !this._hiddenInput) return
    const media = normalizeMedia(this._mediaState)
    this._hiddenInput.value = media ? serializeMedia(media) : ''
    if (media) {
      this.setAttribute('media-value', serializeMedia(media))
      this.setAttribute('value', media.url)
      if (media.filename) this.setAttribute('filename-value', media.filename)
      if (media.position) this.setAttribute('position-value', serializeImagePosition(media.position))
    }
  }

  get accept() {
    return this.getAttribute('accept') || 'image/*'
  }

  get purpose() {
    return this.getAttribute('purpose') || 'content_image'
  }

  get submitOnSuccess() {
    const raw = this.getAttribute('submit-on-success')
    if (raw === null) return false
    return raw !== 'false'
  }

  get submitName() {
    return this.getAttribute('submit-name') || 'submit'
  }

  get submitValue() {
    return this.getAttribute('submit-value') || 'upload'
  }

  get statusPlaceholder() {
    return this.hasAttribute('placeholder') ? this.getAttribute('placeholder') : 'No photo uploaded yet.'
  }

  get previewTarget() {
    return this.getAttribute('preview-target') || ''
  }

  get filenameTarget() {
    return this.getAttribute('filename-target') || ''
  }

  get previewAlt() {
    return this.getAttribute('preview-alt') || 'Uploaded image preview'
  }

  get editorTitle() {
    return this.getAttribute('editor-title') || 'Adjust Photo'
  }

  get cropAspect() {
    return parseFloatAttr(this.getAttribute('crop-aspect'), 4 / 3)
  }

  get cropMode() {
    const raw = this.getAttribute('crop-mode')
    if (raw === null) return ''
    const lower = raw.toLowerCase()
    return lower === 'false' || lower === 'off' || lower === 'none' || lower === '0' ? '' : lower
  }

  get cropEnabled() {
    return !!this.cropMode
  }

  get cropPositionEnabled() {
    return this.cropMode === 'position'
  }

  get cropUploadEnabled() {
    return this.cropMode === 'upload'
  }

  get optimizeImages() {
    const raw = this.getAttribute('optimize-images')
    if (raw === null) return true
    const lower = raw.toLowerCase()
    return lower !== 'false' && lower !== 'off' && lower !== '0'
  }

  get shouldOptimizeImages() {
    if (!this.optimizeImages) return false
    const a = (this.accept || '').toLowerCase()
    return a.includes('image')
  }

  get prepareImageOptions() {
    const attributeMaxEdgePx = parseIntAttr(this.getAttribute('max-edge-px'), 0)

    return {
      maxSourceMb: parseFloatAttr(this.getAttribute('max-source-mb'), DEFAULT_PREPARE_IMAGE_OPTIONS.maxSourceMb),
      maxEdgePx: attributeMaxEdgePx || DEFAULT_PREPARE_IMAGE_OPTIONS.maxEdgePx,
      passthroughMaxKb: parseIntAttr(this.getAttribute('passthrough-max-kb'), DEFAULT_PREPARE_IMAGE_OPTIONS.passthroughMaxKb),
      webpQuality: parseFloatAttr(this.getAttribute('webp-quality'), DEFAULT_PREPARE_IMAGE_OPTIONS.webpQuality),
      imageOptimization: this.optimizeImages
    }
  }

  get value() {
    if (this.opaqueMediaEnabled) {
      return this._mediaState?.url || ''
    }
    return this._hiddenInput?.value || ''
  }

  get filenameValue() {
    if (this.opaqueMediaEnabled) {
      return this._mediaState?.filename || ''
    }
    return this._filenameHiddenInput?.value || ''
  }

  get widthValue() {
    return this._widthInput?.value || ''
  }

  get heightValue() {
    return this._heightInput?.value || ''
  }

  get positionValue() {
    if (this.opaqueMediaEnabled) {
      return this._mediaState?.position
        ? serializeImagePosition(this._mediaState.position)
        : ''
    }
    return this._positionHiddenInput?.value || ''
  }

  /** Serialized opaque media for form serialization (preferred path). */
  get mediaValue() {
    if (!this.opaqueMediaEnabled) return ''
    const media = normalizeMedia(this._mediaState)
    return media ? serializeMedia(media) : ''
  }

  get currentPosition() {
    if (this.opaqueMediaEnabled && this._mediaState?.position) {
      return normalizeImagePosition(this._mediaState.position)
    }
    return normalizeImagePosition(this.positionValue || this.getAttribute('position-value'))
  }

  findTarget(selector) {
    if (!selector) return null

    const scopedRoot = this.closest('anear-form') || this.parentElement || document
    if (scopedRoot?.querySelector) {
      const localMatch = scopedRoot.querySelector(selector)
      if (localMatch) return localMatch
    }

    return document.querySelector(selector)
  }

  updateLinkedPreview(url, filename, position = this.currentPosition) {
    const previewRoot = this.findTarget(this.previewTarget)
    if (previewRoot) {
      const framedClassName =
        previewRoot.getAttribute('data-preview-framed-class')
        || previewRoot.getAttribute('data-preview-img-class')
        || ''

      let framed = previewRoot.matches('anear-framed-image')
        ? previewRoot
        : previewRoot.querySelector('anear-framed-image')

      // First upload often targets a placeholder-only preview root. Use the same
      // display WC as server-rendered views so framing matches after save.
      if (!framed && this.cropPositionEnabled && url) {
        previewRoot.replaceChildren()
        framed = document.createElement('anear-framed-image')
        if (framedClassName) framed.className = framedClassName
        previewRoot.appendChild(framed)
      }

      if (framed) {
        framed.setAttribute('alt', this.previewAlt)
        if (this.opaqueMediaEnabled) {
          const media = normalizeMedia({
            url,
            filename: filename || this.filenameValue || null,
            position: this.cropPositionEnabled || position
              ? normalizeImagePosition(position)
              : null
          })
          if (media) {
            framed.setAttribute('media', serializeMedia(media))
            framed.removeAttribute('src')
            framed.removeAttribute('position')
          } else {
            framed.removeAttribute('media')
            framed.removeAttribute('src')
            framed.removeAttribute('position')
          }
        } else if (url) {
          framed.setAttribute('src', url)
          framed.removeAttribute('media')
          if (this.cropPositionEnabled || this.positionValue) {
            const normalized = normalizeImagePosition(position)
            framed.setAttribute('position', serializeImagePosition(normalized))
          } else {
            framed.removeAttribute('position')
          }
        } else {
          framed.removeAttribute('src')
          framed.removeAttribute('media')
          framed.removeAttribute('position')
        }
      } else {
        let img = previewRoot.matches('img') ? previewRoot : previewRoot.querySelector('img')

        if (!img) {
          previewRoot.replaceChildren()
          img = document.createElement('img')
          const imgClassName = previewRoot.getAttribute('data-preview-img-class') || ''
          if (imgClassName) img.className = imgClassName
          previewRoot.appendChild(img)
        }

        img.src = url
        img.alt = this.previewAlt
        const previewClassName = previewRoot.getAttribute('data-preview-img-class') || ''
        if (previewClassName) {
          img.className = previewClassName
        }
        if (this.cropPositionEnabled || this.positionValue) {
          applyImagePositionToElement(img, position)
        } else {
          applyImagePositionToElement(img, null)
        }
      }
    }

    const filenameEl = this.findTarget(this.filenameTarget)
    if (filenameEl) {
      filenameEl.textContent = filename || ''
    }
  }

  syncInitialPreview() {
    if (!this.value) return
    this.updateLinkedPreview(this.value, this.filenameValue, this.currentPosition)
    this.updateButtons()
  }

  releasePreviewObjectUrl() {
    if (!this._previewObjectUrl) return
    if (typeof URL?.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this._previewObjectUrl)
    }
    this._previewObjectUrl = null
  }

  releaseEditorObjectUrl() {
    if (!this._editorObjectUrl) return
    if (typeof URL?.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this._editorObjectUrl)
    }
    this._editorObjectUrl = null
  }

  showLocalPreview(file) {
    if (!file || typeof URL?.createObjectURL !== 'function') return
    this.releasePreviewObjectUrl()
    this._previewObjectUrl = URL.createObjectURL(file)
    this.updateLinkedPreview(this._previewObjectUrl, file.name)
  }

  syncRemotePreview(url, filename) {
    if (!url) return

    const preloadImg = new Image()
    preloadImg.onload = () => {
      if (this.value !== url) return
      this.releasePreviewObjectUrl()
      this.updateLinkedPreview(url, filename)
    }
    preloadImg.src = url
  }

  ensurePositionHiddenInput() {
    if (this._positionHiddenInput || !this.positionFieldName) return
    this._positionHiddenInput = document.createElement('input')
    this._positionHiddenInput.type = 'hidden'
    this._positionHiddenInput.name = this.positionFieldName
    this._positionHiddenInput.value = ''
    const wrapper = this.querySelector('.anear-image-upload')
    if (wrapper) {
      wrapper.appendChild(this._positionHiddenInput)
    }
  }

  setPosition(position) {
    const normalized = normalizeImagePosition(position)
    const serialized = serializeImagePosition(normalized)

    if (this.opaqueMediaEnabled) {
      this._mediaState = {
        url: this._mediaState?.url || this.value || '',
        filename: this._mediaState?.filename || this.filenameValue || null,
        position: normalized
      }
      this.syncOpaqueHiddenInput()
      if (this._mediaState.url) {
        this.updateLinkedPreview(this._mediaState.url, this._mediaState.filename, normalized)
      }
      return
    }

    this.ensurePositionHiddenInput()
    if (!this._positionHiddenInput) return
    this._positionHiddenInput.value = serialized
    this.setAttribute('position-value', serialized)
    if (this.value) {
      this.updateLinkedPreview(this.value, this.filenameValue, normalized)
    }
  }

  submitClosestForm() {
    if (!this.submitOnSuccess) return
    const form = this.closest('anear-form')
    if (!form) return

    const submitter = {
      name: this.submitName,
      value: this.submitValue,
      getAttribute: (attr) => {
        if (attr === 'name') return this.submitName
        if (attr === 'value') return this.submitValue
        return null
      }
    }

    const submitEvent = new Event('submit', {
      bubbles: true,
      cancelable: true
    })
    Object.defineProperty(submitEvent, 'submitter', {
      value: submitter,
      writable: false,
      configurable: false
    })
    form.dispatchEvent(submitEvent)
  }

  setStatus(text, isError = false) {
    if (!this._statusEl) return
    this._statusEl.textContent = text || ''
    this._statusEl.style.color = isError ? '#b3261e' : '#5c6370'
  }

  setUploading(next) {
    this._uploading = !!next
    if (this._triggerBtn) this._triggerBtn.disabled = this._uploading
    if (this._adjustBtn) this._adjustBtn.disabled = this._uploading || !this.value
    if (this._fileInput) this._fileInput.disabled = this._uploading
  }

  updateButtons() {
    if (this._triggerBtn) {
      const defaultLabel = 'Upload Photo'
      const defaultReplaceLabel = 'Replace Photo'
      const label = this.getAttribute('label') || defaultLabel
      const replaceLabel = this.getAttribute('replace-label') || defaultReplaceLabel
      this._triggerBtn.textContent = this.value ? replaceLabel : label
    }
    if (this._adjustBtn) {
      this._adjustBtn.disabled = this._uploading || !this.value || !this.cropPositionEnabled
      this._adjustBtn.style.display = this.value && this.cropPositionEnabled ? '' : 'none'
    }
  }

  writeDimensionFields(dimensions) {
    const { width, height } = dimensions || {}
    if (this._widthInput) this._widthInput.value = width > 0 ? String(width) : ''
    if (this._heightInput) this._heightInput.value = height > 0 ? String(height) : ''
  }

  /**
   * Dimensions of the blob heading to S3, measured after any optimization or
   * crop so the numbers describe the bytes that actually get stored.
   */
  async measureUpload(blob) {
    if (!this.dimensionsRequested) return null

    return measureImageDimensions(blob)
  }

  dimensionEventDetail(dimensions) {
    if (!this.dimensionsRequested) return {}

    return { width: dimensions?.width || null, height: dimensions?.height || null }
  }

  handleFileSelected(file) {
    if (!file) return
    if (!this.cropEnabled) {
      this.uploadFile(file)
      return
    }
    if (typeof URL?.createObjectURL !== 'function') {
      this.uploadFile(file)
      return
    }

    this._pendingFile = file
    this.releaseEditorObjectUrl()
    this._editorObjectUrl = URL.createObjectURL(file)
    this.openEditor(this._editorObjectUrl)
  }

  openExistingEditor() {
    if (!this.value || !this.cropPositionEnabled) return
    this._pendingFile = null
    this.openEditor(this.value)
  }

  openEditor(imageUrl) {
    this.closeEditor()
    this._modalContainer = document.createElement('div')
    document.body.appendChild(this._modalContainer)
    this._modalRoot = createRoot(this._modalContainer)
    this._modalRoot.render(
      <ImageCropModal
        imageUrl={imageUrl}
        initialPosition={this.currentPosition}
        aspect={this.cropAspect}
        title={this.editorTitle}
        onApply={(result) => this.applyEditorPosition(result)}
        onCancel={() => this.cancelEditor()}
      />
    )
  }

  closeEditor() {
    if (this._modalRoot) {
      this._modalRoot.unmount()
      this._modalRoot = null
    }
    if (this._modalContainer) {
      this._modalContainer.remove()
      this._modalContainer = null
    }
  }

  cancelEditor() {
    this.closeEditor()
    this._pendingFile = null
    this.releaseEditorObjectUrl()
    if (this._fileInput) this._fileInput.value = ''
  }

  async applyEditorPosition({ position, croppedAreaPixels }) {
    const file = this._pendingFile
    if (this.cropPositionEnabled) {
      this.setPosition(position)
    }
    this.closeEditor()
    this.releaseEditorObjectUrl()
    this._pendingFile = null

    if (file) {
      let nextFile = file
      if (this.cropUploadEnabled) {
        try {
          this.setStatus('Cropping photo...')
          nextFile = await cropImageFileForUpload(file, croppedAreaPixels)
        } catch (error) {
          this.setStatus(error?.message || 'Could not crop photo.', true)
          return
        }
      }
      this.uploadFile(nextFile)
    } else {
      this.setStatus(this.filenameTarget ? '' : (this.filenameValue || 'Photo adjusted'))
      this.submitClosestForm()
    }
  }

  async uploadFile(file) {
    if (!file) return
    if (!this._onUploadRequest || !this._eventId) {
      this.setStatus('Uploader not ready. Try again in a moment.', true)
      return
    }

    this.setUploading(true)
    const localDisplayName = file.name
    if (this.opaqueMediaEnabled) {
      this._mediaState = {
        url: '',
        filename: localDisplayName,
        position: this._mediaState?.position || null
      }
      this.syncOpaqueHiddenInput()
    } else {
      this._hiddenInput.value = ''
      if (this._filenameHiddenInput) this._filenameHiddenInput.value = localDisplayName
    }
    // Clear before the round trip so a replacement never submits the dimensions
    // of the photo it replaced.
    this.writeDimensionFields(null)
    this.showLocalPreview(file)

    let uploadFile = file
    let uploadDisplayName = file.name

    try {
      if (this.shouldOptimizeImages) {
        this.setStatus('Optimizing photo...')
        const prepared = await prepareEventImageForUpload(file, this.prepareImageOptions)
        uploadFile = prepared.file
        uploadDisplayName = prepared.displayName
        if (this._filenameHiddenInput) this._filenameHiddenInput.value = localDisplayName
        if (prepared.didProcess) {
          this.showLocalPreview(prepared.file)
        }
      }

      this.setStatus(`Uploading ${localDisplayName}...`)
      const upload = await this._onUploadRequest({
        eventId: this._eventId,
        file: uploadFile,
        purpose: this.purpose
      })

      if (!upload?.uploadUrl || !upload?.finalUrl) {
        throw new Error('Invalid upload response')
      }

      const formData = new FormData()
      const fields = upload.uploadFields || {}
      Object.keys(fields).forEach((key) => formData.append(key, fields[key]))
      formData.append('file', uploadFile)

      const response = await fetch(upload.uploadUrl, {
        method: 'POST',
        body: formData
      })
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`)
      }

      const dimensions = await this.measureUpload(uploadFile)
      this.writeDimensionFields(dimensions)

      if (this.opaqueMediaEnabled) {
        this._mediaState = {
          url: upload.finalUrl,
          filename: localDisplayName,
          position: this._mediaState?.position || null
        }
        this.syncOpaqueHiddenInput()
      } else {
        this._hiddenInput.value = upload.finalUrl
        if (this._filenameHiddenInput) this._filenameHiddenInput.value = localDisplayName
      }
      this.syncRemotePreview(upload.finalUrl, localDisplayName)
      this.updateLinkedPreview(this._previewObjectUrl || upload.finalUrl, localDisplayName)
      this.setStatus(this.filenameTarget ? '' : localDisplayName)
      this.dispatchEvent(new CustomEvent('anear-image-upload:success', {
        bubbles: true,
        detail: {
          url: upload.finalUrl,
          key: upload.key,
          filename: localDisplayName,
          uploadedFilename: uploadDisplayName,
          position: this.positionValue ? normalizeImagePosition(this.positionValue) : null,
          media: this.opaqueMediaEnabled ? normalizeMedia(this._mediaState) : null,
          ...this.dimensionEventDetail(dimensions)
        }
      }))
      this.submitClosestForm()
    } catch (error) {
      if (this.opaqueMediaEnabled) {
        this._mediaState = { url: '', filename: null, position: null }
        this.syncOpaqueHiddenInput()
      } else {
        this._hiddenInput.value = ''
      }
      this.writeDimensionFields(null)
      this.setStatus(error?.message || 'Upload failed', true)
    } finally {
      this.setUploading(false)
      this.updateButtons()
    }
  }

  render() {
    if (!this._fileInput) {
      const wrapper = document.createElement('div')
      wrapper.className = 'anear-image-upload'

      const initialMedia = this.resolveInitialMedia()
      this._mediaState = initialMedia || { url: '', filename: null, position: null }

      this._hiddenInput = document.createElement('input')
      this._hiddenInput.type = 'hidden'
      this._hiddenInput.name = this.fieldName
      if (this.opaqueMediaEnabled) {
        this._hiddenInput.value = initialMedia ? serializeMedia(initialMedia) : ''
      } else {
        this._hiddenInput.value = initialMedia?.url || this.getAttribute('value') || ''
      }

      if (!this.opaqueMediaEnabled && this.filenameFieldName) {
        this._filenameHiddenInput = document.createElement('input')
        this._filenameHiddenInput.type = 'hidden'
        this._filenameHiddenInput.name = this.filenameFieldName
        this._filenameHiddenInput.value = this.getAttribute('filename-value') || initialMedia?.filename || ''
      }

      if (!this.opaqueMediaEnabled && this.positionFieldName) {
        this._positionHiddenInput = document.createElement('input')
        this._positionHiddenInput.type = 'hidden'
        this._positionHiddenInput.name = this.positionFieldName
        const initialPositionValue = this.getAttribute('position-value')
          || (initialMedia?.position ? serializeImagePosition(initialMedia.position) : '')
        this._positionHiddenInput.value = initialPositionValue && initialPositionValue !== 'null'
          ? serializeImagePosition(initialPositionValue)
          : ''
      }

      if (this.widthFieldName) {
        this._widthInput = document.createElement('input')
        this._widthInput.type = 'hidden'
        this._widthInput.name = this.widthFieldName
      }
      if (this.heightFieldName) {
        this._heightInput = document.createElement('input')
        this._heightInput.type = 'hidden'
        this._heightInput.name = this.heightFieldName
      }

      this._fileInput = document.createElement('input')
      this._fileInput.type = 'file'
      this._fileInput.accept = this.accept
      this._fileInput.style.display = 'none'
      this._fileInput.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0]
        this.handleFileSelected(file)
      })

      this._triggerBtn = document.createElement('button')
      this._triggerBtn.type = 'button'
      this._triggerBtn.className = 'btn btn-secondary anear-image-upload-trigger'
      this._triggerBtn.addEventListener('click', () => {
        this._fileInput?.click()
      })

      this._adjustBtn = document.createElement('button')
      this._adjustBtn.type = 'button'
      this._adjustBtn.className = 'btn btn-link anear-image-upload-adjust'
      this._adjustBtn.textContent = this.getAttribute('adjust-label') || 'Adjust Photo'
      this._adjustBtn.addEventListener('click', () => this.openExistingEditor())

      this._statusEl = document.createElement('div')
      this._statusEl.className = 'anear-image-upload-status'
      this._statusEl.textContent = this.statusPlaceholder || ''

      wrapper.appendChild(this._hiddenInput)
      if (this._filenameHiddenInput) wrapper.appendChild(this._filenameHiddenInput)
      if (this._positionHiddenInput) wrapper.appendChild(this._positionHiddenInput)
      if (this._widthInput) wrapper.appendChild(this._widthInput)
      if (this._heightInput) wrapper.appendChild(this._heightInput)
      wrapper.appendChild(this._fileInput)
      wrapper.appendChild(this._triggerBtn)
      wrapper.appendChild(this._adjustBtn)
      wrapper.appendChild(this._statusEl)
      this.appendChild(wrapper)
      this.updateButtons()
    }
  }
}

if (!customElements.get('anear-image-upload')) {
  customElements.define('anear-image-upload', AnearImageUpload)
}

export { normalizeImagePosition, serializeImagePosition }
export default AnearImageUpload
